import { Workbook } from 'exceljs'
import { OUColumn, SummaryByOUResponse } from '../../api'
import { getStatusColor, GLA_STRATEGIC_PRIORITY_LABEL, LEGEND_ENTRIES } from './summaryByOUConstants'

/**
 * Generate and download an Excel file for the Summary by OU data.
 */
export const exportSummaryByOUExcel = async (
  summaryByOUData: SummaryByOUResponse,
  ouColumns: OUColumn[],
  selectedYear: number,
  selectedQuarter: string,
): Promise<void> => {
  const workbook = new Workbook()
  const ws = workbook.addWorksheet('Summary by OU')

  // Header row
  const headerRow = ws.addRow([
    GLA_STRATEGIC_PRIORITY_LABEL,
    'BPD Name',
    'BPD Launched in',
    ...ouColumns.map(col => col.label),
  ])
  headerRow.font = { bold: true }
  headerRow.eachCell(cell => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF5F5F5' },
    }
  })

  // Data rows
  for (const row of summaryByOUData.rows) {
    const launchedIn = row.launched_quarter && row.launched_year ? `${row.launched_quarter}  ${row.launched_year}` : ''
    const dataRow = ws.addRow([
      row.title,
      row.decoded_learning,
      launchedIn,
      ...ouColumns.map(col => row.ou_statuses[col.key] ?? ''),
    ])

    // Apply color to OU status text (starting from column 4)
    ouColumns.forEach((col, idx) => {
      const cellIndex = idx + 4
      const status = row.ou_statuses[col.key]
      const color = getStatusColor(status)
      if (color) {
        const cell = dataRow.getCell(cellIndex)
        cell.font = { color: { argb: 'FF' + color.replace('#', '') }, bold: true }
      }
    })
  }

  // Auto-width columns
  ws.columns.forEach(col => {
    col.width = 20
  })

  // Legend worksheet (driven by shared LEGEND_ENTRIES)
  const legendWs = workbook.addWorksheet('Legend')
  legendWs.addRow(['Status', 'Definition'])
  legendWs.getRow(1).font = { bold: true }
  for (const entry of LEGEND_ENTRIES) {
    legendWs.addRow([entry.status, entry.definition])
  }
  legendWs.columns.forEach(col => {
    col.width = 40
  })

  // Download
  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `Summary_by_OU_${selectedQuarter}_${selectedYear}.xlsx`
  a.click()
  URL.revokeObjectURL(url)
}
